import { createRoute, Link } from '@tanstack/react-router';
import { Monitor, Globe, Heart, HardDrive, ShieldCheck, Key, Download, Apple, Laptop, CheckCircle, WifiOff, Eye, FileOutput, ExternalLink } from 'lucide-react';
import { useState, useEffect } from 'react';
import { usePreferences, applyTheme, setThemeOverride } from '../hooks/usePreferences';
import { useTranslation } from '../i18n';
import type { Locale } from '../i18n';
import { Button } from '../components/ui/Button';
import { useQuery } from '@tanstack/react-query';
import { KOFI_URL, GITHUB_RELEASES_URL, DESKTOP_PRICE } from '../constants/external';
import { apiClient } from '../api/client';
import { Route as rootRoute } from './__root';
import styles from './download.module.css';
import landingStyles from './index.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/download',
  component: DownloadPage,
});

function DownloadPage() {
  const { preferences, setPreference } = usePreferences();
  const { t, locale } = useTranslation();
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const { data: downloadLinks } = useQuery({
    queryKey: ['downloadLinks'],
    queryFn: () => apiClient.getDownloadLinks(),
    staleTime: 5 * 60 * 1000,
  });

  async function handleDesktopCheckout() {
    setCheckoutLoading(true);
    try {
      const { checkoutUrl } = await apiClient.createDesktopCheckout();
      window.location.href = checkoutUrl;
    } catch {
      setCheckoutLoading(false);
    }
  }

  // Force Vivid theme on public pages, restore user's theme on leave
  useEffect(() => {
    setThemeOverride(true);
    applyTheme('vivid');
    return () => {
      setThemeOverride(false);
      applyTheme(preferences.theme);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleLocale = (next: Locale) => setPreference('locale', next);

  const platforms = [
    {
      Icon: Apple,
      title: t('download.macTitle'),
      desc: t('download.macDesc'),
      button: t('download.macButton'),
      href: downloadLinks?.mac ?? GITHUB_RELEASES_URL,
    },
    {
      Icon: Monitor,
      title: t('download.winTitle'),
      desc: t('download.winDesc'),
      button: t('download.winButton'),
      href: downloadLinks?.windows ?? GITHUB_RELEASES_URL,
    },
    {
      Icon: Laptop,
      title: t('download.linuxTitle'),
      desc: t('download.linuxDesc'),
      button: t('download.linuxButton'),
      href: downloadLinks?.linux?.deb ?? GITHUB_RELEASES_URL,
    },
  ];

  const features = [
    { Icon: WifiOff, title: t('download.feature1Title'), desc: t('download.feature1Desc') },
    { Icon: ShieldCheck, title: t('download.feature2Title'), desc: t('download.feature2Desc') },
    { Icon: Key, title: t('download.feature3Title'), desc: t('download.feature3Desc') },
    { Icon: FileOutput, title: t('download.feature4Title'), desc: t('download.feature4Desc') },
    { Icon: HardDrive, title: t('download.feature5Title'), desc: t('download.feature5Desc') },
    { Icon: Eye, title: t('download.feature6Title'), desc: t('download.feature6Desc') },
  ];

  const steps = [
    t('download.step1'),
    t('download.step2'),
    t('download.step3'),
    t('download.step4'),
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.page}>
      {/* Controls — reuse landing page pattern */}
      <div className={landingStyles.controls}>
        <div className={landingStyles.langPill} role="group" aria-label="Language">
          <Globe size={13} strokeWidth={1.75} className={landingStyles.langIcon} />
          {(['en', 'he'] as Locale[]).map((loc) => (
            <button
              key={loc}
              className={`${landingStyles.langOption} ${locale === loc ? landingStyles.langOptionActive : ''}`}
              onClick={() => toggleLocale(loc)}
              aria-pressed={locale === loc}
            >
              {loc.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Hero */}
      <section className={styles.hero}>
        <Link to="/" className={styles.backLink}>
          <span className={styles.heroPrompt}>&gt; </span>
          SiliconVault
        </Link>
        <h1 className={styles.heroTitle}>{t('download.heroTitle')}</h1>
        <p className={styles.heroTagline}>{t('download.heroTagline')}</p>
        <div className={styles.priceBadge}>{t('download.priceBadge')}</div>
      </section>

      {/* Get Started — two-column: Download + License */}
      <section className={styles.getStarted}>
        <div className={styles.getStartedGrid}>
          {/* Left column: Download */}
          <div className={styles.getStartedCol}>
            <h2 className={styles.colTitle}>
              <span className={styles.colStep}>1.</span> {t('download.platformsTitle')}
            </h2>
            <div className={styles.downloadCard}>
              {platforms.map(({ Icon, title, desc, button, href }, i) => (
                <div key={title}>
                  <div className={styles.platformRow}>
                    <span className={styles.platformIcon}>
                      <Icon size={20} strokeWidth={1.5} />
                    </span>
                    <div className={styles.platformInfo}>
                      <h3 className={styles.platformName}>{title}</h3>
                      <p className={styles.platformDesc}>{desc}</p>
                    </div>
                    <a href={href} target="_blank" rel="noopener noreferrer" className={styles.platformLink}>
                      <Button size="small">{button}</Button>
                    </a>
                  </div>
                  {i < platforms.length - 1 && <div className={styles.platformDivider} />}
                </div>
              ))}
              <div className={styles.platformDivider} />
              <a
                href={GITHUB_RELEASES_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.githubLink}
              >
                <ExternalLink size={13} strokeWidth={1.75} />
                <span>All releases on GitHub</span>
              </a>
            </div>
          </div>

          {/* Right column: License */}
          <div className={styles.getStartedCol}>
            <h2 className={styles.colTitle}>
              <span className={styles.colStep}>2.</span> {t('download.purchaseTitle')}
            </h2>
            <div className={styles.licenseCard}>
              <Key size={28} strokeWidth={1.5} className={styles.licenseIcon} />
              <p className={styles.licenseDesc}>{t('download.licenseCardDesc')}</p>
              <button
                className={styles.purchaseLink}
                onClick={handleDesktopCheckout}
                disabled={checkoutLoading}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
              >
                <Button>{checkoutLoading ? 'Loading…' : t('download.purchaseButton')}</Button>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('download.howTitle')}</h2>
        <div className={styles.stepsList}>
          {steps.map((step, i) => (
            <div key={i} className={styles.step}>
              <span className={styles.stepNumber}>
                <CheckCircle size={16} strokeWidth={2} />
              </span>
              <span className={styles.stepText}>{step}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Why Desktop? — feature highlights */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('download.featuresTitle')}</h2>
        <div className={styles.featureGrid}>
          {features.map(({ Icon, title, desc }) => (
            <div key={title} className={styles.featureCard}>
              <span className={styles.featureIcon}>
                <Icon size={16} strokeWidth={1.75} />
              </span>
              <div>
                <h3 className={styles.featureCardTitle}>{title}</h3>
                <p className={styles.featureCardDesc}>{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <span className={styles.footerText}>
          &copy; {new Date().getFullYear()} Asaf Saar. {t('download.footer')}
        </span>
        <span className={styles.footerSep}> | </span>
        <Link to="/pricing" className={styles.footerLink}>{t('pricing.footerLink')}</Link>
        <span className={styles.footerSep}> | </span>
        <Link to="/terms" className={styles.footerLink}>{t('footer.terms')}</Link>
        <span className={styles.footerSep}> | </span>
        <Link to="/privacy" className={styles.footerLink}>{t('footer.privacy')}</Link>
        <span className={styles.footerSep}> | </span>
        <Link to="/refund" className={styles.footerLink}>{t('footer.refund')}</Link>
        <span className={styles.footerSep}> | </span>
        <a
          href={KOFI_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.footerSupport}
        >
          <Heart size={10} strokeWidth={2} />
          {t('support.footerLink')}
        </a>
      </footer>
    </div>
  );
}
